import React, { useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { View, StyleSheet } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import FormLayout from "../ActivityForms/common/FormLayout";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { actions, RichEditor, RichToolbar } from "react-native-pell-rich-editor";

const MAX_MODULES = 35;
const MAX_ACTIVITIES = 5;
const MIN_MODULES = 1;
const MIN_ACTIVITIES = 1;

const courseGeneratorSettingsSchema = z.object({
  numberOfModules: z.coerce
    .number()
    .min(MIN_MODULES, `Number of modules must be at least ${MIN_MODULES}`)
    .max(MAX_MODULES, `Number of modules cannot exceed ${MAX_MODULES}`),
  numberOfActivities: z.coerce
    .number()
    .min(MIN_ACTIVITIES, `Number of activities per module must be at least ${MIN_ACTIVITIES}`)
    .max(MAX_ACTIVITIES, `Number of activities per module cannot exceed ${MAX_ACTIVITIES}`),
  courseDescription: z.string().optional(),
});

type CourseGeneratorSettingsFormData = z.infer<typeof courseGeneratorSettingsSchema>;

interface CourseGeneratorSettingsProps {
  onSubmit: (data: CourseGeneratorSettingsFormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const CourseGeneratorSettings = ({ onSubmit, onCancel, isSubmitting }: CourseGeneratorSettingsProps) => {
  const { t } = useTranslation();
  const refInstance = useRef<RichEditor | null>(null);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CourseGeneratorSettingsFormData>({
    defaultValues: {
      numberOfModules: 1,
      numberOfActivities: 1,
      courseDescription: "",
    },
    resolver: zodResolver(courseGeneratorSettingsSchema),
  });

  return (
    <View style={{ flex: 1, width: "100%" }}>
      <FormLayout
        title="Course Generator Content"
        subtitle="To help us generate the best content for your course, please provide a few details below. You can also upload any documents or materials you would like axio to include."
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={handleSubmit(onSubmit)}
        actionText="Generate Course"
        error={errors?.numberOfModules?.message || errors?.numberOfActivities?.message}>
        <CustomText text="Course Description" size="s" weight="ultraBold" />
        <CustomText
          text="Provide a brief description of the course's purpose and goals."
          size="xs"
          style={{ paddingTop: 4, color: "#667085" }}
          useTranslationText={false}
        />

        <CustomSpacing type="vertical" size="s" />
        <CustomSpacing type="vertical" size="s" />

        <Controller
          name="courseDescription"
          control={control}
          defaultValue=""
          render={({ field: { onChange, onBlur, value } }) => (
            <CustomTextInput
              placeholder={t(
                "text.Example: This course introduces students to foundational concepts in biology, including cell structure, genetics and ecology."
              )}
              multiline
              numberOfLines={5}
              onBlur={onBlur}
              onChangeText={onChange}
              value={value}
              style={{ width: "100%", minHeight: 120, textAlignVertical: "top" }}
              error={errors?.courseDescription?.message}
            />
          )}
        />
        <CustomSpacing type="vertical" size="s" />
        <CustomText text="Number of Modules" size="s" weight="ultraBold" />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          name="numberOfModules"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <CustomTextInput
              placeholder="Enter number of modules"
              onBlur={onBlur}
              onChangeText={(text) => onChange(text)}
              value={value?.toString()}
              style={{ width: "100%" }}
              keyboardType="numeric"
              inputMode="numeric"
              error={errors.numberOfModules?.message}
            />
          )}
        />
        <CustomSpacing type="vertical" size="xl" />
        <CustomText text="Number of Activities per Module" size="s" weight="ultraBold" />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          name="numberOfActivities"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <CustomTextInput
              placeholder="Enter number of activities per module"
              onBlur={onBlur}
              onChangeText={(text) => onChange(text)}
              value={value?.toString()}
              style={{ width: "100%" }}
              keyboardType="numeric"
              inputMode="numeric"
              error={errors.numberOfActivities?.message}
            />
          )}
        />
      </FormLayout>
    </View>
  );
};
const styles = StyleSheet.create({
  editorContainer: {
    flex: 1,
    marginBottom: 10,
    minHeight: 110,
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: "#D1D5DB",
    borderEndEndRadius: 10,
    borderEndStartRadius: 10,
    overflow: "hidden",
  },
  editor: {
    flex: 1,
  },
  toolbar: {
    backgroundColor: "#F2F4F7",
    borderColor: "#D1D5DB",
    borderWidth: 1,
    borderBottomWidth: 0,
    borderTopRightRadius: 10,
    borderTopLeftRadius: 10,
  },
});

export default CourseGeneratorSettings;
